import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';

interface MobileRecordingContextType {
  isMobileRecordingMode: boolean;
  isPaused: boolean;
  setMobileRecordingMode: (active: boolean) => void;
  setPaused: (paused: boolean) => void;
  onPauseRecording?: () => void;
  onResumeRecording?: () => void;
  onGenerateSummary?: () => void;
  setRecordingCallbacks: (callbacks: {
    onPause?: () => void;
    onResume?: () => void;
    onGenerateSummary?: () => void;
  }) => void;
}

const MobileRecordingContext = createContext<
  MobileRecordingContextType | undefined
>(undefined);

export const useMobileRecording = () => {
  const context = useContext(MobileRecordingContext);
  if (!context) {
    throw new Error(
      'useMobileRecording must be used within a MobileRecordingProvider'
    );
  }
  return context;
};

interface MobileRecordingProviderProps {
  children: ReactNode;
}

export const MobileRecordingProvider: React.FC<
  MobileRecordingProviderProps
> = ({ children }) => {
  const [isMobileRecordingMode, setMobileRecordingMode] = useState(false);
  const [isPaused, setPaused] = useState(false);
  const [onPauseRecording, setOnPauseRecording] = useState<
    (() => void) | undefined
  >();
  const [onResumeRecording, setOnResumeRecording] = useState<
    (() => void) | undefined
  >();
  const [onGenerateSummary, setOnGenerateSummary] = useState<
    (() => void) | undefined
  >();

  const setRecordingCallbacks = (callbacks: {
    onPause?: () => void;
    onResume?: () => void;
    onGenerateSummary?: () => void;
  }) => {
    setOnPauseRecording(() => callbacks.onPause);
    setOnResumeRecording(() => callbacks.onResume);
    setOnGenerateSummary(() => callbacks.onGenerateSummary);
  };

  const value = useMemo(() => ({
    isMobileRecordingMode,
    isPaused,
    setMobileRecordingMode,
    setPaused,
    onPauseRecording,
    onResumeRecording,
    onGenerateSummary,
    setRecordingCallbacks
  }), [
    isMobileRecordingMode,
    isPaused,
    onPauseRecording,
    onResumeRecording,
    onGenerateSummary
  ]);

  return (
    <MobileRecordingContext.Provider value={value}>
      {children}
    </MobileRecordingContext.Provider>
  );
};
